/**
 * Which carrier should take a collection, from its boxes and its postcode.
 *
 * The ladder itself lives in `shared/carrierAssignment.js`, shared with customer
 * returns. This file measures a deal (package string, address) and asks it.
 *
 * Lifted out of Collections.jsx when the No Tracking exception lane needed the
 * same answer: the board asks "who should we book?", the exception lane asks
 * "who did we most likely use?" for a collection already booked without the
 * carrier being recorded. Two screens, one ladder — a second copy would drift,
 * and a carrier is not cosmetic here. It picks the customer's email template
 * (`selectEmailTemplate`) and builds the tracking link (`buildTrackingUrl`).
 *
 * One caveat every caller inherits: `assignCarrier` does not abstain. Handed a
 * deal with no parseable `package_details` it reasons from a zero weight and an
 * empty box list and still names a carrier — ParcelForce for a courier, Shiply
 * for a drop-off. That answer is worth nothing, so every return carries
 * `hasPackageData` to say whether a box list was behind it.
 */

import { routeShipment } from '../../../shared/carrierAssignment.js'

const CM_PER_INCH = 2.54

/**
 * Pick a carrier for a deal.
 *
 * @param {object} deal - needs `package_details`, `collection_address`,
 *   `has_printer` ('Yes'/'No', as HubSpot stores it) and `send_type`.
 * @returns {{carrierId: string, reason: string, hasPackageData: boolean, notAllowed?: boolean}}
 *   `hasPackageData` false means the answer was reasoned from nothing — show it
 *   as a guess or not at all.
 */
export function assignCarrier(deal) {
    const boxes = parsePackageDetails(deal.package_details)?.boxes || []
    const verdict = routeShipment({
        boxes,
        // HubSpot stores has_printer as a 'Yes'/'No' string.
        hasPrinter: deal.has_printer?.toLowerCase() === 'yes',
        postcode: resolvedAddressFields(deal).postcode || null,
        sendType: deal.send_type
    })
    return { ...verdict, hasPackageData: boxes.length > 0 }
}

/**
 * Parse a `package_details` string into boxes and a total weight.
 * Format: "Box 1: 14kg - 50 x 50 x 50 cm" or "Box 1: .5kg - 18 x 10 x 17 inches"
 *
 * @returns {{boxes: object[], totalWeight: number}|null} null when nothing parses.
 */
export function parsePackageDetails(packageString) {
    if (!packageString) return null

    // Accepts .5kg, 0.5kg, 5kg, 5kgs AND cm/inch/inches/in - dimensions can also be decimals
    const boxMatches = packageString.match(/Box\s*\d+:\s*(\.?\d+(?:\.\d+)?)\s*kgs?\s*-\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*(cm|inch|inches|in)/gi)
    if (!boxMatches) return null

    const boxes = []
    boxMatches.forEach((match, index) => {
        const parts = match.match(/(\.?\d+(?:\.\d+)?)\s*kgs?\s*-\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i)
        if (!parts) return

        // '.5kg' is half a kilo; parseFloat would read it fine but the leading
        // zero keeps the value readable everywhere it is echoed back.
        const weightStr = parts[1].startsWith('.') ? `0${parts[1]}` : parts[1]
        const multiplier = match.toLowerCase().includes('inch') ? CM_PER_INCH : 1

        boxes.push({
            boxNumber: index + 1,
            weight: parseFloat(weightStr),
            length: Math.round(parseFloat(parts[2]) * multiplier),
            width: Math.round(parseFloat(parts[3]) * multiplier),
            height: Math.round(parseFloat(parts[4]) * multiplier)
        })
    })

    if (boxes.length === 0) return null

    return { boxes, totalWeight: boxes.reduce((sum, box) => sum + (box.weight || 0), 0) }
}

/**
 * Pull a UK postcode out of a free-text address, or null.
 *
 * FALLBACK-ONLY: Prism resolves the postcode server-side (see
 * `resolvedAddressFields`). This regex runs only when `resolved_address` is
 * absent, so it is no longer exported — the one client-side parse lives inside
 * `resolvedAddressFields`.
 */
function extractPostcodeFromAddress(address) {
    if (!address) return null
    const match = address.match(/[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}/i)
    return match ? match[0] : null
}

/**
 * The collection address split into label fields — resolved-first.
 *
 * Prism resolves every collection address server-side into `resolved_address`
 * (structured-when-present, else the legacy raw string parsed once — proven
 * identical to the old client parser over the whole prod corpus, gate
 * 2026-09-10). Read that. The raw-string fallback below runs only when the
 * field is absent (an old cached deal, or a path that predates the Phase-3
 * shim forwarding). Prism_Interface has no staging and this feeds courier
 * labels, so the guarded fallback stays rather than risk a blank line or
 * postcode. This is the single client-side address parse site.
 *
 * `source` is a deal / shipment carrying `resolved_address` and the raw
 * `collection_address`. Returns `{ line1, line2, city, postcode }`.
 */
export function resolvedAddressFields(source) {
    const r = source?.resolved_address
    if (r) {
        return {
            line1: r.line_1 || '',
            line2: r.line_2 || '',
            city: r.town || '',
            postcode: r.postcode || '',
        }
    }
    const address = source?.collection_address
    const postcode = extractPostcodeFromAddress(address) || ''
    if (!address) return { line1: '', line2: '', city: '', postcode: '' }
    const withoutPostcode = address.replace(postcode, '').replace(/,\s*$/, '').trim()
    const parts = withoutPostcode.split(',').map(p => p.trim()).filter(Boolean)
    if (parts.length === 0) return { line1: '', line2: '', city: '', postcode }
    if (parts.length === 1) return { line1: parts[0], line2: '', city: '', postcode }
    return {
        line1: parts[0],
        line2: parts.slice(1, -1).join(', '),
        city: parts[parts.length - 1],
        postcode,
    }
}
